import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { darken, lighten, alpha, Typography } from "@mui/material";

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';

import ButtonBase from '@mui/material/ButtonBase';
import Tooltip, {tooltipClasses} from '@mui/material/Tooltip';
import {isNullOrNaN} from '../../Bs4Components/Bs4Utils';

import * as Bs4Utils from '../../Bs4Components/Bs4Utils'
import { getBoxSx, getBoxStyle } from '../../Bs4Theme/Bs4UxTemplates';
import { SmallScreenContext, useStateUxTemplate } from '../../Bs4Theme/Bs4UxTemplates';

import { useDraggable } from '@dnd-kit/core';
import { v4 as uuidv4 } from 'uuid';

import {    
  compareAsc, compareDesc,
  differenceInMilliseconds,
  addMilliseconds,
} from "date-fns";
import { Bolt } from '@mui/icons-material';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
)) (({ theme, boxsx }) => { 
  return({
  [`& .${tooltipClasses.tooltip}`]: {
    ...boxsx,
    //maxWidth: 220,    
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: boxsx?.backgroundColor,
    "&::before": {
        ...boxsx,    
      }
  },
})});

const HtmlTooltipCanOpen = (props) => {
  const {canOpen, ...other} = props;
  const [open, setOpen] = React.useState(false)
  const handleOpen = (event) => {setOpen(canOpen);}
  const handleClose = (event) => {setOpen(false);}

  return (
    <HtmlTooltip
      open={open && canOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      {...other}
    >
      {props.children}
    </HtmlTooltip>
  );
}

const ResizeHolder = (props) => {
  const [startPosition, setStartPosition] = React.useState(null);  

  const handlePointerDown = React.useCallback((event) => {          
    if (event.pointerType === 'touch') return;
    event.stopPropagation();
    const startPosition = {x: event.pageX, y: event.pageY};
    setStartPosition(startPosition);
    props?.onStart(startPosition, event);
  }, [setStartPosition, props?.onStart]);

  const handlePointerMove = React.useCallback((event) => {          
    if (event.pointerType === 'touch') return;      
    if (!startPosition) return;
    event.stopPropagation();
    props?.onMove({x: event.pageX, y: event.pageY});
    props.scrollBy(event.pageX, event.pageY);
  }, [startPosition, props?.onMove]);

  const handlePointerUp = React.useCallback((event) => {      
    if (event.pointerType === 'touch') return;
    if (!startPosition) return;
    event.stopPropagation();
    setStartPosition(null);
    props?.onStop();    
  }, [startPosition, setStartPosition, props?.onStop]);

  const handleTouchStart = React.useCallback((event) => {
    event.stopPropagation();
    const touch = event.touches[0];
    const startPosition = {x: touch.pageX, y: touch.pageY};
    setStartPosition(startPosition);
    props?.onStart(startPosition, touch);
  }, [props?.onStart, setStartPosition]);

  const handleTouchMove = React.useCallback((event) => {      
    if (!startPosition) return;    
    event.stopPropagation();
    const touch = event.touches[0];
    const newPosition = {x: touch.pageX, y: touch.pageY};
    props?.onMove(newPosition);    
    props.scrollBy(touch.pageX, touch.pageY);
  }, [props?.onMove, startPosition]);
  
  const handleTouchEnd = React.useCallback((event) => {
    if (!startPosition) return;
    event.stopPropagation();
    setStartPosition(null);
    props?.onStop();
  }, [props?.onStop, startPosition, setStartPosition]);

  const handleKeyDown  = React.useCallback((event) => {
    if (!startPosition) return;
    if (event.code !== 'Escape') return;
    setStartPosition(null);
    props?.onCancel();
  }, [props?.onCancel, startPosition, setStartPosition]);

  React.useEffect(() => {    
    if (!props.disabled) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);

      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);

      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);

      window.removeEventListener('keydown', handleKeyDown);
    }
  }, [props.disabled, handlePointerMove, handlePointerUp, handleTouchMove, handleTouchEnd]);

  if (props.disabled) return null;

  return (
    <Box 
      sx={{
        position: 'relative',
        ...props.sx, 
        touchAction: 'none',
        overflow: 'visible',
      }} 
      onPointerDown={handlePointerDown}
      onTouchStart={handleTouchStart}
    />      
  )
}


const DRAGGABLE = 'draggable';

const svgBanP = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path fill="red" d="M512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256zM92.93 126.9C64.8 162.3 48 207.2 48 256C48 370.9 141.1 464 256 464C304.8 464 349.7 447.2 385.1 419.1L92.93 126.9zM464 256C464 141.1 370.9 48 256 48C207.2 48 162.3 64.8 126.9 92.93L419.1 385.1C447.2 349.7 464 304.8 464 256z"/></svg>';
const svgBan =  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path fill="red" d="M367.2 412.5L99.5 144.8C77.1 176.1 64 214.5 64 256c0 106 86 192 192 192c41.5 0 79.9-13.1 111.2-35.5zm45.3-45.3C434.9 335.9 448 297.5 448 256c0-106-86-192-192-192c-41.5 0-79.9 13.1-111.2 35.5L412.5 367.2zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256z"/></svg>';
const svgCheck = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><!--!Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/></svg>';

const borderMain = {
  borderWidth: 0.2,
  borderStyle: 'solid',
  // borderColor: '#00000066',
  borderRadius: 1,
  ///boxShadow: '0px 0px 0px 1px rgba(255, 255, 255, 1)',
}

export const ViewBs4EventDragOverlay = (props) => {
  const [anchorPosition, setAnchorPosition] = React.useState(null);
  const theme = useTheme();

  const canDragBs4Event = props.bs4Event.chtime || props.bs4Event.chaxis;

  const boxSx = React.useMemo(() => {          
    return {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
      ...getBoxStyle(borderMain, theme),
      ...getBoxStyle(props.uxBodyEvent, theme),
      ...getBoxStyle(props.bs4Event?.xx555, theme),
      boxShadow: theme.shadows[5],
      opacity: 0.5,
    };
  }, [theme, props?.bs4Event?.xx555]);

  React.useEffect(() => {
    const handlePointerMove = (event) => {      
      // console.log('...Move Pointer', event);
      // Czasem działa Pointer a czasem Touch ??? !!!
      // ??? !!! if (event.pointerType === 'touch') return;
      setAnchorPosition({ top: event.clientY, left: event.clientX })
    };

    const handleTouchMove = (event) => {
      // console.log('...Move Touch', event);
      const touch = event.touches[0];
      setAnchorPosition({ top: touch.clientY, left: touch.clientX })
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('touchmove', handleTouchMove);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener('touchmove', handleTouchMove);
    }
  }, [setAnchorPosition]);

  const [moveIcon, menuOptions, startIcon, endIcon] = React.useMemo(() => {
    const moveAll = <i className="fa-regular fa-arrows-up-down-left-right"></i>;
    const moveHorizontally = <i className="fa-regular fa-arrows-left-right"></i>;
    const moveVertically = <i className="fa-solid fa-arrows-up-down"></i>;
    const resizeLeft = <i className="fa-regular fa-arrow-left-from-line"></i>;
    const resizeRight = <i className="fa-regular fa-arrow-right-from-line"></i>;
    const resizeUp = <i className="fa-regular fa-arrow-up-from-line"></i>;
    const resizeDown = <i className="fa-regular fa-arrow-down-from-line"></i>;
    const menuOptions = props.bs4Event.chaxis0 && !props.bs4Event?.move?.cursor ? <i className="fa-regular fa-ellipsis"></i> : null;
    
    let moveIcon = null;
    let startIcon = null;
    let endIcon = null;

    if (canDragBs4Event) switch (props.bs4Event?.move?.cursor) {
        case 'n': 
          startIcon = resizeUp;
          break;
        case 's': 
          endIcon = resizeDown;
          break;
        case 'w':  
          startIcon = resizeLeft;
          break;
        case 'e':  
          endIcon = resizeRight;
          break;
        default: 
          if (props.bs4Event.chtime && !props.bs4Event.chaxis) moveIcon = moveHorizontally;          
          if (!props.bs4Event.chtime && props.bs4Event.chaxis) moveIcon = moveVertically;
          if (props.bs4Event.chtime && props.bs4Event.chaxis) moveIcon = moveAll;
          break;
    }

    return [moveIcon, menuOptions, startIcon, endIcon];
  }, [props.bs4Event?.move?.cursor, canDragBs4Event, props.bs4Event.chaxis])

  const [startTime, endTime] = React.useMemo(() => {
    return [
      canDragBs4Event ? <Typography variant='body2'>{Bs4Utils.toDateMinutes(props.bs4Event.start)}</Typography> : null, 
      canDragBs4Event ? <Typography variant='body2'>{Bs4Utils.toDateMinutes(props.bs4Event.end)}</Typography> : null,
    ];
  }, [canDragBs4Event, props.bs4Event.start, props.bs4Event.end])  

  return (
    <Popover
      disableEscapeKeyDown
      open={Boolean(anchorPosition)}        
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      anchorOrigin={{vertical: 'top', horizontal: 'left',}}
      transformOrigin={{vertical: 'bottom', horizontal: 'left',}}
      slotProps={{paper: {sx: {backgroundColor: 'transparent', }},}}
      sx={{cursor: props?.bs4Event?.move?.visibleCursor || null,}}
    >      
    <Stack 
      direction="column"
      spacing={0} 
      sx={{
        justifyContent: "flex-start", 
        alignItems: "flex-start", 
        ...boxSx,
        padding: 1,
        wordBreak: 'keep-all',
        fontSize: 10,
      }}
    >        
      <Stack direction="row" spacing={1} sx={{justifyContent: "flex-start", alignItems: "flex-start",}}>
        {moveIcon} {menuOptions} {startIcon}
      </Stack>
      {startTime} {endTime} {endIcon}
    </Stack>
    </Popover>
  )
}

export const ViewBs4EventDraggable = React.memo((props) => {
  const setActive = React.useCallback((dragging) => {
    props.bs4Event.move.active = dragging;
    if (props?.onClose) props.onClose();
  }, [props.bs4Event]);

  const {
    setNodeRef, 
    setActivatorNodeRef, 
    listeners, 
    // isDragging,
    node,
    // over,
  } = useDraggable({
    id: DRAGGABLE+'_'+props.bs4Event.key, 
    disabled: !(Boolean(props.bs4Event.chaxis0) || Boolean(props.bs4Event.chaxis) || Boolean(props.bs4Event.chtime)),
    data: {
      props: props,
      setActive: setActive,      
    },
  });    

  return (      
    <ViewBs4Event 
      {...props} 
      setNodeRef={setNodeRef} 
      setActivatorNodeRef={setActivatorNodeRef}
      listeners={listeners}
    />
  )
});

export const ViewBs4Event = React.memo((props) => {
  const theme = useTheme();
  
  const [select, setSelect] = React.useState(false);
  const [isTouch, setIsTouch] = React.useState(false);
  const [eventChangeTime, setEventChangeTime] = React.useState(new Date());
  const [resizeStart, setResizeStart] = React.useState(false);    

  const ref = React.useRef(null);
  const refTouchStart = React.useRef({
    event: null,
    timeout: null,
  })
  
  const canDragToOptions = Boolean(props.bs4Event.chaxis0);
  const canDragToResource = Boolean(props.bs4Event.chaxis);
  const canDragBs4Event = Boolean(props.bs4Event.chtime);
  const canDrag = !props.bs4Event?.layer && (canDragToOptions || canDragBs4Event);
  
  const enableDrop = React.useMemo(() => {
    if (!props.over?.updateId?.id) return false;
    if (!props.bs4Event?.ResourceId) return false;
    if (props.over.updateId.menuItem) return true;

    if (props.bs4Event?.chtime && props.bs4Event?.chaxis) return true;
    if (props.bs4Event?.chtime && !props.bs4Event?.chaxis) return props.over.updateId.id === props.bs4Event.ResourceId;
    if (!props.bs4Event?.chtime && props.bs4Event?.chaxis) return true; 
    return false;
  }, [props.over]);

  if (props.over) props.bs4Event.enableDrop = enableDrop;

  const cursorSx = React.useMemo(() => {
      const cursorCss = {};    
      if (props.bs4Event?.move) props.bs4Event.move.visibleCursor = null;
      if (!(Boolean(props.bs4Event.chaxis0) || Boolean(props.bs4Event.chtime))) return cursorCss; 
      cursorCss.cursor = 'grab';
      if (props.bs4Event.move?.active) cursorCss.cursor = 'grabbing';        
      if (props.bs4Event?.move) props.bs4Event.move.visibleCursor = cursorCss.cursor;
      return cursorCss;
    }, [props.bs4Event.chaxis0, props.bs4Event.chtime, props.bs4Event.move?.active])  
  
  const boxSx = React.useMemo(() => {          
    const updateSx = {};
    if (compareAsc(props.bs4Timeline.start, props.bs4Event.start) >= 0) {
      updateSx.borderTopLeftRadius = '0px';
      updateSx.borderBottomLeftRadius = '0px';
      updateSx.borderLeftStyle = "none";
    }
    if (compareDesc(props.bs4Timeline.end, props.bs4Event.end) >= 0) {
      updateSx.borderTopRightRadius = '0px';
      updateSx.borderBottomRightRadius = '0px';
      updateSx.borderRightStyle = "none";
    }

    if (props.over) {
      updateSx.opacity = 0.33;
    }

    if (props.bs4Event?.move?.new) {
      updateSx.opacity = 0.5;
    }

    // <i class="fa-solid fa-ban"></i>

    // if (props.over && !enableDrop) {
    //   updateSx['&::after'] = {          
    //     content: '""',
    //     backgroundImage: `url('data:image/svg+xml;utf8,${svgBan}')`,
    //     backgroundRepeat: 'no-repeat',
    //     backgroundOrigin: 'content-box',
    //     backgroundColor: '#ffffff88',
    //     position: 'absolute',
    //     top: 0,
    //     left: 0,
    //     padding: '4px',
    //     height: '100%',
    //     width: '100%',
    //   }
    // }

    const boxStyle = {
      ...getBoxStyle(props.uxBodyEvent, theme),
      ...getBoxStyle(props.bs4Event.xx555, theme),
    }

    const backgroundColor = boxStyle.backgroundColor || theme.palette.primary.light;
    delete boxStyle.backgroundColor
    if (props.bs4Event?.progressP > 0) {
      const backgroundColorDarken = darken(backgroundColor, 0.1);
      const backgroundColorLighten = lighten(backgroundColor, 0.1);
      boxStyle.background = `linear-gradient(to right, ${backgroundColorDarken} 0%,  ${backgroundColorDarken} ${props.bs4Event?.progressP}%,  ${backgroundColorLighten} ${props.bs4Event?.progressP}%,  ${backgroundColorLighten} 100%)`;
    } else {
      boxStyle.backgroundColor = backgroundColor;
    }
    // const sx = getBoxStyle(props.uxBodyEvent, theme);
    
    return {
      // backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
      borderRadius: 0.5,
      ...boxStyle,
      ...updateSx,
    };
  }, [props.bs4Timeline, props.uxBodyEvent, props.bs4Event?.xx555, props.bs4Event?.progressP, props.over, theme]);

  const [[initResizeDate, resizeRect], setInitResizeDate] = React.useState([null, null])

  const [percentStart, percentEnd, percentWidth] = React.useMemo(() => {
    props.bs4Timeline.updateBs4EventView(props.bs4Event);
    const timelineStartTime = props.bs4Timeline.start.getTime();
    const timelineEndTime = props.bs4Timeline.end.getTime();

    const eventStartTime = Math.max(timelineStartTime, props.bs4Event.startView.getTime());
    const eventEndTime = Math.min(timelineEndTime, props.bs4Event.endView.getTime());
  
    const percentStart = ((1.0 * (eventStartTime - timelineStartTime)) / (timelineEndTime - timelineStartTime)) * 100.0;
    const percentEnd   = ((1.0 * (timelineEndTime - eventEndTime)) / (timelineEndTime - timelineStartTime)) * 100.0;
    const percentWidth = ((1.0 * (eventEndTime - eventStartTime)) / (timelineEndTime - timelineStartTime)) * 100.0;
    return [percentStart, percentEnd, percentWidth];
  }, [eventChangeTime, props.timelineChangeTime, props.activeChangeTime, props.bs4Event.start, props.bs4Event.end, props.over]);
  
  const resizeEventStartLeft = (startPosition, event) => {
    resizeEventStart(startPosition, 'w', event);
  }

  const resizeEventStartRight = (startPosition, event) => {            
    resizeEventStart(startPosition, 'e', event);
  }

  const resizeEventStart = (startPosition, cursor, event) => {
    if (!props.nodeParent) {
      console.log('...Error...resizeEventStart...!props.nodeParent', startPosition, props.bs4Event);
      return;
    }
    
    updateMove(event, cursor);
    props.bs4Event.move.start = props.bs4Event.start;
    props.bs4Event.move.end = props.bs4Event.end;
    props.bs4Event.move.duration = props.bs4Event.duration;
    props.bs4Event.move.visibleCursor = 'ew-resize';

    const resizeRect = props.nodeParent?.getBoundingClientRect();
    const milliseconds = (startPosition.x - resizeRect.x) * differenceInMilliseconds(props.bs4Timeline.end, props.bs4Timeline.start) / resizeRect.width;
    const initResizeDate = addMilliseconds(props.bs4Timeline.start, milliseconds); 
    setInitResizeDate([initResizeDate, resizeRect]);
    setResizeStart(true);
  }

  const resizeEventMoveLeft = (newPosition) => {
    const rect = props.nodeParent?.getBoundingClientRect();
    if (!rect) return;
    
    const timeToWidthRatio = differenceInMilliseconds(props.bs4Timeline.end, props.bs4Timeline.start) / rect.width;
    const date = addMilliseconds(props.bs4Timeline.start, timeToWidthRatio * (newPosition.x - rect.x));
    const [movedDate, d] = props.bs4Timeline.moveDate(props.bs4Event.start, date);
    if (compareAsc(props.bs4Event.end, movedDate) >= 0 && compareAsc(props.bs4Event.start, movedDate) !== 0) {        
      props.bs4Event.start = movedDate;
      props.bs4Timeline.updateBs4EventView(props.bs4Event);
      setEventChangeTime(new Date());
      return;
    }        
  }

  const resizeEventMoveRight = (newPosition) => {     
    const rect = props.nodeParent?.getBoundingClientRect();
    if (!rect) return;
    
    const timeToWidthRatio = differenceInMilliseconds(props.bs4Timeline.end, props.bs4Timeline.start) / rect.width;
    const date = addMilliseconds(props.bs4Timeline.start, timeToWidthRatio * (newPosition.x - rect.x));
    const [movedDate, d] = props.bs4Timeline.moveDate(props.bs4Event.end, date);
    if (compareDesc(props.bs4Event.start, movedDate) >= 0 && compareAsc(props.bs4Event.end, movedDate) !== 0) {
      props.bs4Event.end = movedDate;
      props.bs4Timeline.updateBs4EventView(props.bs4Event);
      setEventChangeTime(new Date());
      return;
    }
  }

  const resizeEventStop = () => {
    setResizeStart(false);
    setSelect(false);    
    props.bs4Timeline.bs4EventResize(props);
  }

  const resizeEventCancel = () => {
    setResizeStart(false);
    setSelect(false);    
    props.bs4Event.start = props.bs4Event.move.start;
    props.bs4Event.end = props.bs4Event.move.end;
    props.bs4Event.duration = props.bs4Event.move.duration;
    props.bs4Timeline.updateBs4EventView(props.bs4Event);
  }

  const handleClick = (event) => {            
    // console.log('...???...TimeLine.ViewEvent.Click', props.bs4Event.move, props.bs4Event);
    setSelect(false);
    try {
      props.onRowClick(props.bs4Event);
    } catch(error) {}
  }    
  
  const [tooltipViewTitle, tooltipTitle] = React.useMemo(() => {
    const tooltipTitle = ((props.bs4Event.bs4fill2 || props.bs4Event.Name)+'').trim();
    return [(
      <Typography
        dangerouslySetInnerHTML={{__html: tooltipTitle}}
        onClick={handleClick}
      />
    ), tooltipTitle]

  }, [props.bs4Event.bs4fill2, props.bs4Event.Name])

  const handleSelect = (event) => {
    setIsTouch(event.pointerType === 'touch');
    if (event.pointerType === 'touch') return;
    setSelect(event.type === 'pointerenter');
  }
  
  const holderWidth = isTouch ? 24 : 8;
  const holderOffset = isTouch ? 23 : 8;    
  const padding = 0.1;
  const background = 'linear-gradient(to left, transparent 20%, #00000033 20% 40%, transparent 40% 60%, #00000033 60% 80%, transparent 80%)'    
  const sx = React.useMemo(() => {
    const rect = props.nodeParent?.getBoundingClientRect();
    const top =  props.bs4Event?.move?.active ? 0 : props.bs4Timeline.bs4EventHeight * props.bs4Event.line;
    const height = props.bs4Event?.move?.active ? '100%' : props.bs4Timeline.bs4EventHeight - props.bs4Timeline.borderWidth;
    if (!rect) return {};
    return { 
      position: 'absolute', 
      top: top, 
      left: `${percentStart}%`, 
      right: `calc(${percentEnd}% + ${props.bs4Timeline.borderWidth}px)`, 
      height: height,
      display: 'flex',
      flexDirection: 'row',
      userSelect: 'none',
      zIndex: resizeStart ? 500 : null,        
    }
  }, [props.nodeParent, props.bs4Event?.line, resizeStart, percentWidth, percentStart, percentEnd])
  
  // props.bs4Event.chtime = true;    

  // const handlePointerUpListeners = (event) => {
  //   if (this.bs4EventPointerDownListeners.id && !resizeStart) event.stopPropagation();
  // }

  const visibility = (select && !props.over && !props.bs4Event?.move?.new) ? 'visible' : 'hidden';

  const updateMove = React.useCallback((event, cursor) => {
    props.bs4Event.move = {
      key: props.bs4Event.key,
      parentKey: props.bs4Event.parentKey,
      start: props.bs4Event.start,
      
      end: props.bs4Event.end,
      duration: props.bs4Event.duration,
      ResourceId: props.bs4Event.ResourceId,
      moveDate: null,
      cursor: null,
    };
    const rect = ref.current.getBoundingClientRect();
    const differenceInMillisecondsStartToEnd = differenceInMilliseconds(props.bs4Event.endView, props.bs4Event.startView);            
    const size = rect.width;
    const offsetFromOrigin = event.pageX - rect.left;
    const offsetFromOriginInMilliseconds = (differenceInMillisecondsStartToEnd / size) * offsetFromOrigin;    
    // moveDate = data cursora (miejsce kursora w momencie chwytu)
    props.bs4Event.move.moveDate = addMilliseconds(props.bs4Event.startView, offsetFromOriginInMilliseconds);
    
    props.bs4Event.move.cursor = cursor;

    if (props.bs4Event.move.cursor) {
      if (cursor === 'n' || cursor === 'w') props.bs4Event.move.moveDate = props.bs4Event.startView;
      if (cursor === 's' || cursor === 'e') props.bs4Event.move.moveDate = props.bs4Event.endView;    
    }
  }, [props.bs4Event, ref.current]);

  const handlePointerDown = React.useCallback((event) => {    
    setIsTouch(event.pointerType === 'touch');
    if (event.pointerType === 'touch') return;
    event.stopPropagation();
    // console.log('...???...TimeLine.ViewEvent.PointerDown',);
    updateMove(event, null);
    props.listeners?.onPointerDown(event);
    setSelect(false);
  },[setSelect, updateMove, props.listeners]);

  const handlePointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    // console.log('...???...TimeLine.PointerMove', )
  }

  const handlePointerUp = (event) => {
    if (event.pointerType === 'touch') return;
    // console.log('...???...TimeLine.PointerUp', )    
  }

  const handleTouchStart = (event) => {
    event.stopPropagation();
    // console.log('...???...TimeLine.TouchStart', );
    clearTimeout(refTouchStart.current.timeout); 
    if (select) {
      refTouchStart.current.event = null;
      updateMove(event.touches[0], null);
      props.listeners?.onTouchStart(event);
      setSelect(false);
      return;
    } 
    refTouchStart.current.event = event;
    refTouchStart.current.timeout = setTimeout(() => {
      refTouchStart.current.event = null;        
      setSelect(true);        
      // console.log('...???...TimeLine.ViewEvent.TouchStart SELECTED!!!!!!!!!!!', );
    }, 500);
  }

  const handleTouchMove = (event) => {
    // console.log('...???...TimeLine.TouchMove', )
    clearTimeout(refTouchStart.current.timeout);
    if (refTouchStart.current.event) {
      // console.log('...???...TimeLine.TouchMove >!>!>!>!>>>>>!!!!!>>!>>!>!', )
      // event.stopPropagation();
      updateMove(event.touches[0], null);
      props.listeners?.onTouchStart(refTouchStart.current.event);  
      refTouchStart.current.event = null;
      setSelect(false);
    }  
  }

  const handleTouchEnd = (event) => {
    // console.log('...???...TimeLine.TouchEnd', )
    clearTimeout(refTouchStart.current.timeout);      
  }

  const handleTouchCancel = (event) => {
    // console.log('...???...TimeLine.TouchCancel', )
    clearTimeout(refTouchStart.current.timeout);      
  }

  React.useEffect(() => {       
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);      
    window.addEventListener('pointercancel', handlePointerUp);      

    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchCancel);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);

      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchCancel);
    }
  }, [handlePointerMove, handlePointerUp, handleTouchMove, handleTouchEnd]);

  const canOpenTooltip = !(Boolean(props.noTooltip) || Boolean(props.bs4Event?.move?.active) || Boolean(props.bs4Event?.move?.new) || resizeStart || !tooltipTitle);

  return (
    <HtmlTooltipCanOpen
      boxsx={{
        ...boxSx,
        boxShadow: theme.shadows[3],
        overflow: 'inherit',
      }}
      canOpen={canOpenTooltip}      
      arrow
      placement='top'
      title={tooltipViewTitle}
    >
      <Box 
        ref={node => {
          if (props.setNodeRef) props.setNodeRef(node);
          ref.current = node;
        }} 
        sx={{
          ...sx, 
          touchAction: 'none',
        }}
        onPointerEnter={handleSelect}
        onPointerLeave={handleSelect}          
      >              
        <ResizeHolder
          disabled={!props.bs4Event.chtime}
          bs4Event={props.bs4Event}
          scrollBy={props.scrollBy}
          sx={{
            position: 'absolute', 
            visibility: visibility,
            top: 0, 
            bottom: 0,
            left: -holderOffset,
            width: holderWidth,
            background: background,
            cursor: 'ew-resize',
            zIndex: 2000,
          }}
          onStart={resizeEventStartLeft}
          onMove={resizeEventMoveLeft}
          onStop={resizeEventStop}
          onCancel={resizeEventCancel}
        />
        <Box            
          flex={1}
          overflow='hidden'
          ref={props.setActivatorNodeRef}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onTouchStart={handleTouchStart}
          sx={{
            ...boxSx, 
            ...cursorSx,
            position: 'relative',
          }}
        >
          {props.bs4Event?.move?.new ? null : <Typography dangerouslySetInnerHTML={{__html: props.bs4Event.Name}}/> }
        </Box>        
        {resizeStart || props.bs4Event?.move?.new ? <ViewBs4EventDragOverlay bs4Event={props.bs4Event} /> : null}
        <ResizeHolder
          disabled={!props.bs4Event.chtime}
          bs4Event={props.bs4Event}
          scrollBy={props.scrollBy}
          sx={{
            position: 'absolute', 
            visibility: visibility,
            top: 0, 
            bottom: 0,
            right: -holderOffset, 
            width: holderWidth,
            background: background,
            cursor: 'ew-resize',
            zIndex: 2000,
          }}
          onStart={resizeEventStartRight}
          onMove={resizeEventMoveRight}
          onStop={resizeEventStop}
          onCancel={resizeEventCancel}
        />
      </Box>
    </HtmlTooltipCanOpen>
  );
});